import { runInAction } from 'mobx'
import type { $RootStore } from '../../src/stores/RootStore'
import Communication from '../../shared-lib/communication/Communication'
import getApiUrl from '../../shared-lib/communication/getApiUrl'

export type ApiUrls = {
  apiUrl: string
  wsUrl: string
  graphUrl: string
}

export type CommunicationSetup = {
  apiDefault: string
  platform: string
  appVersion: string
  useSecure: string
  override?: string
  timezone: string
  env: string
  addNetworkLatency?: number
  deviceip?: string
  resubscribeActiveListeners?: () => Promise<void>
}

export const openCommunication = async (
  rootStore: $RootStore,
  setup: CommunicationSetup,
): Promise<{ communication: Communication, urls: ApiUrls } | null> => {
  let urls: ApiUrls

  try {
    urls = await getApiUrl({
      apiDefault: setup.apiDefault,
      platform: setup.platform,
      appVersion: setup.appVersion,
      useSecure: setup.useSecure,
      override: setup.override,
    })
  } catch (e) {
    return null
  }

  const communication = new Communication(rootStore, {
    apiUrl: urls.apiUrl,
    graphUrl: urls.graphUrl,
    wsUrl: urls.wsUrl,
    platform: setup.platform,
    env: setup.env,
    timezone: setup.timezone,
    addNetworkLatency: setup.addNetworkLatency ?? 0,
    deviceip: setup.deviceip,
    resubscribeActiveListeners: setup.resubscribeActiveListeners,
  })

  return { communication, urls }
}

export type ConfigResponse = {
  settings?: { settings?: Record<string, any> | null } | null
  appVer?: any
  checkBlockedUser?: boolean | null
} | undefined

export type ApplyConfigOptions = {
  requireAppVer: boolean
  onFailure: () => void
}

export const applyConfigResponse = (
  rootStore: $RootStore,
  response: ConfigResponse,
  { requireAppVer, onFailure }: ApplyConfigOptions,
): boolean => {
  const remoteConfig = response?.settings
  const appVer = response?.appVer
  const blocked = response?.checkBlockedUser

  if (rootStore.authStore.sessionExpired) return false

  if (!remoteConfig || blocked === undefined || blocked === null || (requireAppVer && !appVer)) {
    onFailure()
    return false
  }

  runInAction(() => {
    rootStore.remoteConfig = remoteConfig.settings ?? {}
    rootStore.appVer = appVer ?? { apiVersion: null }
  })

  rootStore.i18n.setCostSettings(rootStore.remoteConfig['costForFeature'] ?? {})
  rootStore.userStore.set({ blocked })

  if (blocked) rootStore.authStore.logout_api()

  return true
}

export const loadSharedSession = async (rootStore: $RootStore) => {
  const { isReceptionSession } = rootStore.authStore

  await rootStore.userStore.updateUserTraits_api()

  await rootStore.salonStore.loadSalon_api()

  if (!isReceptionSession) {
    await rootStore.salonStore.loadMySalonMemberships_api()
    rootStore.salonStore.listenOnSalonMemberUpdated_api()
  }

  await rootStore.externalAddressStore.fetchAddressesForBookingLocations(rootStore.userStore.validLocations)
}

export const SHARED_STORES = [
  'authStore',
  'userStore',
  'salonStore',
  'agendaStore',
  'bookingStore',
  'consultationFormStore',
  'crmStore',
  'emailCampaignStore',
  'smsCampaignStore',
  'externalAddressStore',
  'externalBookingStore',
  'externalClientStore',
  'externalGiftCardStore',
  'externalPromocodeStore',
  'externalReviewStore',
  'externalTreatmentLibrary',
  'externalTreatmentStore',
  'googleBusinessProfileStore',
  'professionalAvailabilityStore',
  'setupStore',
  'statisticsStore',
  'subscriptionStore',
  'webPageStore',
] as const satisfies readonly (keyof $RootStore)[]

export const clearSharedStores = (rootStore: $RootStore) => {
  SHARED_STORES.forEach(key => rootStore[key].clearData())
}

export const clearGraphQLCache = async (rootStore: $RootStore) => {
  await rootStore.communication?.client.cache.reset()
  rootStore.communication?.forceWsReconnect()
}
